import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { useFormik } from 'formik';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip, { ChipProps } from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { DataGrid, GridAlignment, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import ManageSearchOutlined from '@mui/icons-material/ManageSearchOutlined';
import OpenInNewOutlined from '@mui/icons-material/OpenInNewOutlined';
import PeopleAltOutlined from '@mui/icons-material/PeopleAltOutlined';
import PersonSearchOutlined from '@mui/icons-material/PersonSearchOutlined';

import { Formatter, FormInput, Loader, theme } from '@omnigenbiodata/ui';

import PortalLayout from '../../../../../../components/layouts/PortalLayout';
import ROUTES from '../../../../../../core/constants/routes.constants';
import { ParticipantStatus } from '../../../../../../core/types/common.types';
import { useAppDispatch, useAppSelector } from '../../../../../../store';
import {
  hasSuccessSelector,
  isBusySelector,
  participantSearchThunk,
  responseSelector,
} from '../../../../../../store/participants';
import PageBanner from '../../../../../../components/layouts/PageBanner';

const primaryTint = theme.palette.grey[100];
const primaryTintStrong = theme.palette.grey[100];
const successTint = theme.palette.success.lighter;

interface ParticipantSearchValues {
  keyword: string;
}

interface ParticipantSearchRow {
  participantId: string;
  participantRef: string;
  email: string;
  firstName: string;
  surname: string;
  sexAtBirth: string;
  nhsNumber: string;
  dob: string;
  participantStatus: string;
}

const validationSchema = yup.object().shape({
  keyword: yup.string().trim().required('Enter a search term').min(3, 'Enter at least 3 characters'),
});

function getParticipantStatusColour(status: string | null | undefined): ChipProps['color'] {
  if (
    status === ParticipantStatus.WITHDRAWN ||
    status === ParticipantStatus.DECEASED ||
    status === ParticipantStatus.SUSPENDED
  ) {
    return 'error';
  }

  if (status === ParticipantStatus.ACTIVE) {
    return 'success';
  }

  if (!status) {
    return 'default';
  }

  return 'primary';
}

function getColumns(onViewParticipant: (participantId: string) => void): GridColDef[] {
  return [
    {
      field: 'participantRef',
      headerName: 'Ref',
      flex: 0.8,
      minWidth: 130,
    },
    {
      field: 'firstName',
      headerName: 'First Name',
      flex: 1,
      minWidth: 140,
    },
    {
      field: 'surname',
      headerName: 'Surname',
      flex: 1,
      minWidth: 140,
    },
    {
      field: 'email',
      headerName: 'Email',
      flex: 1.4,
      minWidth: 220,
    },
    {
      field: 'sexAtBirth',
      headerName: 'Sex',
      flex: 0.7,
      minWidth: 100,
    },
    {
      field: 'nhsNumber',
      headerName: 'NHS No',
      flex: 1,
      minWidth: 140,
    },
    {
      field: 'dob',
      headerName: 'DOB',
      flex: 0.8,
      minWidth: 130,
      renderCell: (params: GridRenderCellParams<ParticipantSearchRow, string>) =>
        params.value ? Formatter({ input: params.value, format: 'friendlyDate' }) : 'Not recorded',
    },
    {
      field: 'participantStatus',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 130,
      align: 'center' as GridAlignment,
      headerAlign: 'center' as GridAlignment,
      renderCell: (params: GridRenderCellParams<ParticipantSearchRow, string>) => (
        <Chip
          label={params.value || 'UNKNOWN'}
          color={getParticipantStatusColour(params.value)}
          size="small"
          sx={{ fontWeight: 700 }}
          data-testid="status-chip"
        />
      ),
    },
    {
      field: 'participantId',
      headerName: '',
      flex: 0.5,
      minWidth: 110,
      sortable: false,
      filterable: false,
      align: 'center' as GridAlignment,
      headerAlign: 'center' as GridAlignment,
      renderCell: (params: GridRenderCellParams<ParticipantSearchRow, string>) => (
        <Button
          size="small"
          variant="outlined"
          endIcon={<OpenInNewOutlined />}
          onClick={(event: SyntheticEvent) => {
            event.stopPropagation();

            if (params.value) {
              onViewParticipant(params.value);
            }
          }}
        >
          View
        </Button>
      ),
    },
  ];
}

function EmptyResultsPanel() {
  return (
    <Paper
      elevation={0}
      sx={{
        mt: 3,
        border: 1,
        borderColor: 'rgba(16, 53, 68, 0.12)',
        bgcolor: primaryTint,
        overflow: 'hidden',
      }}
    >
      <Box sx={{ height: 6, bgcolor: 'primary.main' }} />

      <Box
        sx={{
          p: 3,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            bgcolor: 'background.paper',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
          aria-hidden="true"
        >
          <PersonSearchOutlined color="primary" sx={{ fontSize: 30 }} />
        </Box>

        <Box sx={{ minWidth: 0 }}>
          <Typography variant="h6" component="h2" sx={{ fontWeight: 700, color: 'primary.main', mb: 0.5 }}>
            No participants to display
          </Typography>

          <Typography variant="body2" component="p" color="text.secondary" sx={{ lineHeight: 1.5 }}>
            Enter a participant reference, name, NHS number or email address to search for matching participant records.
          </Typography>
        </Box>
      </Box>
    </Paper>
  );
}

function ParticipantsScene() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const isBusy = useAppSelector(isBusySelector);
  const hasSuccess = useAppSelector(hasSuccessSelector);
  const response = useAppSelector(responseSelector);

  const results = Array.isArray(response) ? response : response?.items ?? [];
  const nextToken = Array.isArray(response) ? null : response?.nextToken ?? null;

  const [lastKeyword, setLastKeyword] = useState('');

  const handleViewParticipant = useCallback(
    (participantId: string) => {
      navigate(ROUTES.portalParticipant.replace(':participantId', participantId));
    },
    [navigate],
  );

  const columns = useMemo(() => getColumns(handleViewParticipant), [handleViewParticipant]);

  const formik = useFormik<ParticipantSearchValues>({
    initialValues: { keyword: '' },
    validationSchema,
    onSubmit: (values) => {
      const keyword = values.keyword.trim();

      setLastKeyword(keyword);

      dispatch(
        participantSearchThunk({
          keyword,
          nextToken: null,
        }),
      );
    },
  });

  const handleLoadMore = () => {
    if (!nextToken || !lastKeyword) {
      return;
    }

    dispatch(
      participantSearchThunk({
        keyword: lastKeyword,
        nextToken,
      }),
    );
  };

  const hasResults = results.length > 0;
  const hasSearched = Boolean(lastKeyword) || hasSuccess;

  return (
    <PortalLayout>
      <Loader label="Loading Participants" isVisible={isBusy} />

      <Box pb={3}>
        <PageBanner
          title="Participants"
          description="Search, review and manage participant records, consent status, EHR status and linked sample activity."
          extras={
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: 'background.paper',
                border: 1,
                borderColor: 'rgba(16, 53, 68, 0.12)',
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              <Box
                sx={{
                  width: 44,
                  height: 44,
                  borderRadius: 2,
                  bgcolor: successTint,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}
                aria-hidden="true"
              >
                <PeopleAltOutlined color="primary" sx={{ fontSize: 28 }} />
              </Box>

              <Box sx={{ minWidth: 0 }}>
                <Typography variant="overline" component="p" sx={{ color: 'text.secondary', fontWeight: 700 }}>
                  Participant administration
                </Typography>

                <Typography variant="body2" component="p" color="text.secondary">
                  Use search to locate a participant record.
                </Typography>
              </Box>
            </Box>
          }
        />

        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Paper
              elevation={2}
              sx={{
                border: 1,
                borderColor: 'rgba(16, 53, 68, 0.18)',
                bgcolor: 'background.paper',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ height: 6, bgcolor: 'primary.main' }} />

              <Box
                sx={{
                  p: { xs: 2, md: 3 },
                  bgcolor: primaryTintStrong,
                  borderBottom: 1,
                  borderColor: 'rgba(16, 53, 68, 0.12)',
                }}
              >
                <Stack spacing={2}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box
                      sx={{
                        width: 44,
                        height: 44,
                        borderRadius: 2,
                        bgcolor: 'background.paper',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                      aria-hidden="true"
                    >
                      <ManageSearchOutlined color="primary" sx={{ fontSize: 28 }} />
                    </Box>

                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="h6" component="h2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                        Search participants
                      </Typography>

                      <Typography variant="body2" component="p" color="text.secondary">
                        Search by participant reference, name, NHS number or email address.
                      </Typography>
                    </Box>
                  </Box>

                  <Box component="form" onSubmit={formik.handleSubmit}>
                    <Grid container spacing={2} alignItems="flex-start">
                      <Grid item xs={12} md={9}>
                        <FormInput
                          name="keyword"
                          label="Search term"
                          value={formik.values.keyword}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.errors.keyword}
                          touched={formik.touched.keyword}
                        />
                      </Grid>

                      <Grid
                        item
                        xs={12}
                        md={3}
                        sx={{
                          pt: { md: '30px !important' },
                        }}
                      >
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          size="large"
                          disabled={isBusy}
                          startIcon={<ManageSearchOutlined />}
                          sx={{
                            width: '100%',
                            minHeight: 56,
                            fontWeight: 700,
                          }}
                        >
                          Search
                        </Button>
                      </Grid>
                    </Grid>
                  </Box>
                </Stack>
              </Box>

              <Box p={{ xs: 2, md: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2 }}>
                  <Box>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                      Search results
                    </Typography>

                    <Typography variant="body2" component="p" color="text.secondary">
                      {hasResults
                        ? `${results.length} participant record${results.length === 1 ? '' : 's'} shown`
                        : hasSearched
                          ? 'No matching participant records found'
                          : 'No search has been run yet'}
                    </Typography>
                  </Box>

                  {hasResults && (
                    <Chip
                      label={`${results.length} result${results.length === 1 ? '' : 's'}`}
                      size="small"
                      data-testid="hasResults"
                      sx={{
                        bgcolor: successTint,
                        color: 'primary.main',
                        fontWeight: 700,
                      }}
                    />
                  )}
                </Box>

                {hasResults ? (
                  <>
                    <Box
                      sx={{
                        width: '100%',
                        border: 1,
                        borderColor: 'rgba(16, 53, 68, 0.12)',
                        borderRadius: 1,
                        overflow: 'hidden',
                        '& .MuiDataGrid-columnHeaders': {
                          bgcolor: primaryTint,
                          color: 'primary.main',
                          fontWeight: 700,
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                          fontWeight: 700,
                        },
                        '& .MuiDataGrid-row:hover': {
                          bgcolor: primaryTint,
                        },
                      }}
                    >
                      <DataGrid
                        autoHeight
                        rows={results}
                        columns={columns}
                        getRowId={(row) => row.participantId}
                        disableRowSelectionOnClick
                        disableVirtualization={process.env.NODE_ENV === 'test'}
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                          pagination: {
                            paginationModel: {
                              page: 0,
                              pageSize: 10,
                            },
                          },
                        }}
                        onRowDoubleClick={(params) => {
                          if (params.row.participantId) {
                            handleViewParticipant(params.row.participantId);
                          }
                        }}
                      />
                    </Box>

                    {nextToken && (
                      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                        <Button
                          variant="outlined"
                          color="primary"
                          endIcon={<ArrowForwardOutlined />}
                          onClick={handleLoadMore}
                          disabled={isBusy}
                          sx={{
                            fontWeight: 700,
                          }}
                        >
                          Load more results
                        </Button>
                      </Box>
                    )}
                  </>
                ) : (
                  <EmptyResultsPanel />
                )}
              </Box>
            </Paper>
          </Grid>
        </Grid>
      </Box>
    </PortalLayout>
  );
}

export default ParticipantsScene;
